// In-memory drafts для survey-шагов. Source of truth: docs/specs/sprint-3/stage-4-screens.md §6.
//
// Q1 (stage-4 §1.1): drafts только в React Context, не в localStorage —
// PII не пишем в persistent storage без consent. Reload → drafts стираются,
// defaultValues подставляются из profile.survey.step{N}_{key} (см. §5.10).

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  Step1Form,
  Step2Form,
  Step3Form,
  Step4Form,
  Step5Form,
} from '@/api/schemas';

export type SurveyDrafts = {
  step1?: Step1Form;
  step2?: Step2Form;
  step3?: Step3Form;
  step4?: Step4Form;
  step5?: Step5Form;
};

export type DraftFor<N extends 1 | 2 | 3 | 4 | 5> = N extends 1
  ? Step1Form
  : N extends 2
    ? Step2Form
    : N extends 3
      ? Step3Form
      : N extends 4
        ? Step4Form
        : N extends 5
          ? Step5Form
          : never;

export type SurveyContextValue = {
  drafts: SurveyDrafts;
  saveDraft: <N extends 1 | 2 | 3 | 4 | 5>(step: N, payload: DraftFor<N>) => void;
  clearDraft: (step: 1 | 2 | 3 | 4 | 5) => void;
  reset: () => void;
};

const KEY_BY_N: Record<1 | 2 | 3 | 4 | 5, keyof SurveyDrafts> = {
  1: 'step1',
  2: 'step2',
  3: 'step3',
  4: 'step4',
  5: 'step5',
};

const SurveyContext = createContext<SurveyContextValue | null>(null);

export function SurveyProvider({ children }: { children: ReactNode }) {
  const [drafts, setDrafts] = useState<SurveyDrafts>({});

  const saveDraft = useCallback(
    <N extends 1 | 2 | 3 | 4 | 5>(step: N, payload: DraftFor<N>) => {
      const key = KEY_BY_N[step];
      setDrafts((prev) => ({ ...prev, [key]: payload }));
    },
    [],
  );

  const clearDraft = useCallback((step: 1 | 2 | 3 | 4 | 5) => {
    const key = KEY_BY_N[step];
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const reset = useCallback(() => setDrafts({}), []);

  const value = useMemo<SurveyContextValue>(
    () => ({ drafts, saveDraft, clearDraft, reset }),
    [drafts, saveDraft, clearDraft, reset],
  );

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
}

export function useSurveyContext(): SurveyContextValue {
  const ctx = useContext(SurveyContext);
  if (!ctx) {
    throw new Error('useSurveyContext must be used within <SurveyProvider>');
  }
  return ctx;
}
